<script setup lang="ts">
import { computed, toRef } from 'vue';
import { i18n, t } from '~/i18n';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  hreflangFor,
  normalizeSiteLocale,
  stripLocalePrefix,
} from '@/lib/site-locale';

const locale = toRef(i18n.options, 'locale');
const route = useRoute();
const siteLocale = computed(() => normalizeSiteLocale(locale.value) ?? 'en');
const unprefixedPath = computed(() => stripLocalePrefix(route.path));
const isLocalizedContentDetail = computed(() =>
  /^\/(?:blog|spotlight|ai-design-tools)\/(?!page\/|categories\/)[^/]+\/?$/.test(
    unprefixedPath.value
  )
);
const shouldLoadJapaneseDisplayFont = computed(() =>
  String(locale.value || 'en')
    .toLowerCase()
    .startsWith('ja')
);

useHead(() => ({
  title: t('landing_home.seo.title'),
  htmlAttrs: {
    lang: locale.value,
  },
  meta: [
    { key: 'description', name: 'description', content: t('landing_home.seo.description') },
    { key: 'keywords', name: 'keywords', content: t('landing_home.seo.keywords') },
    { key: 'author', name: 'author', content: 'Veeso AI' },
    { key: 'language', name: 'language', content: hreflangFor(siteLocale.value) },
  ],
  link: shouldLoadJapaneseDisplayFont.value
    ? [
        {
          rel: 'preconnect',
          href: 'https://fonts.googleapis.com',
          key: 'jp-display-font-preconnect-googleapis',
        },
        {
          rel: 'preconnect',
          href: 'https://fonts.gstatic.com',
          crossorigin: 'anonymous',
          key: 'jp-display-font-preconnect-gstatic',
        },
        {
          rel: 'stylesheet',
          href: 'https://fonts.googleapis.com/css2?family=Dela+Gothic+One&display=swap',
          key: 'jp-display-font-stylesheet',
        },
        ...(isLocalizedContentDetail.value
          ? []
          : [
              {
                rel: 'canonical',
                href: absoluteLocalizedUrl(siteLocale.value, unprefixedPath.value),
                key: 'canonical',
                tagPriority: 'high' as const,
              },
              ...buildPathHreflangLinks(unprefixedPath.value),
            ]),
      ]
    : isLocalizedContentDetail.value
      ? []
      : [
          {
            rel: 'canonical',
            href: absoluteLocalizedUrl(siteLocale.value, unprefixedPath.value),
            key: 'canonical',
            tagPriority: 'high' as const,
          },
          ...buildPathHreflangLinks(unprefixedPath.value),
        ],
  script: [
    {
      key: 'organization-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Veeso AI',
        alternateName: ['Veeso', 'Veeso.ai', 'Veeso App'],
        url: 'https://veeso.ai',
        logo: 'https://veeso.ai/images/veesoai.png',
        description: t('landing_home.seo.description'),
        sameAs: [
          'https://twitter.com/veesoai',
          'https://www.linkedin.com/company/veesoai',
          'https://github.com/veesoai',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'hi@veeso.ai',
          contactType: t('ui.site.customerSupport'),
          availableLanguage: [hreflangFor(siteLocale.value)],
        },
      }),
    },
    {
      key: 'software-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Veeso AI',
        alternateName: ['Veeso', 'Veeso.ai', 'Veeso App'],
        applicationCategory: 'DesignApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: t('ui.site.freeTierAvailable'),
        },
        description: t('landing_home.seo.description'),
        featureList: [
          t('ui.site.features.aiDesignGeneration'),
          t('ui.site.features.socialMediaTemplates'),
          t('ui.site.features.instagramCarousel'),
          t('ui.site.features.youtubeThumbnail'),
          t('ui.site.features.linkedinPost'),
          t('ui.site.features.brandKit'),
        ],
        screenshot: 'https://veeso.ai/images/veeso-screenshot.png',
        softwareVersion: '1.0',
        author: { '@type': 'Organization', name: 'Veeso AI', url: 'https://veeso.ai' },
      }),
    },
  ],
}));

const AuthModalClient = import.meta.client
  ? (await import('@/components/auth/AuthModal.vue')).default
  : null;
const ToasterClient = import.meta.client ? (await import('@/components/ui/toast')).Toaster : null;
const CookieBannerClient = import.meta.client
  ? (await import('@/components/CookieBanner.vue')).default
  : null;

// 在页面渲染前立即检测系统偏好设置，避免闪烁
// 暂时注释掉系统暗黑模式检测功能
// useHead({
//   script: [
//     {
//       textContent: `(function(){const prefersDark=window.matchMedia('(prefers-color-scheme: dark)');const updateDarkMode=(isDark)=>{if(isDark){document.documentElement.classList.add('dark');}else{document.documentElement.classList.remove('dark');}};updateDarkMode(prefersDark.matches);if(prefersDark.addEventListener){prefersDark.addEventListener('change',(e)=>updateDarkMode(e.matches));}else{prefersDark.addListener((e)=>updateDarkMode(e.matches));}})();`,
//       type: 'text/javascript',
//       tagPosition: 'head',
//     },
//   ],
// });
</script>

<template>
  <NuxtRouteAnnouncer />
  <NuxtLayout>
    <!-- Avoid stale state when reusing route components (e.g. /spotlight/:slug) -->
    <NuxtPage :key="$route.fullPath" />
  </NuxtLayout>
  <ClientOnly>
    <component :is="AuthModalClient" v-if="AuthModalClient" />
    <component :is="ToasterClient" v-if="ToasterClient" />
    <component :is="CookieBannerClient" v-if="CookieBannerClient" />
  </ClientOnly>
</template>

<style>
/* Page transition */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}
</style>
